import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Cookie } from '@quizzach/assets';
import { RoutePath } from '@quizzach/interfaces';
import { BtnLine, BtnTeal, CookieOptions, TextLink } from '@quizzach/styles';
import { useCookieConsent } from '@quizzach/contexts';
import { ModalShell, Switch } from '@quizzach/components';

/**
 * Cookie consent in the shared modal shell (same shape as the auth
 * dialogs). No onClose on purpose: the dialog can't be dismissed
 * without picking a consent option. The consent cookies written here
 * are unchanged.
 */
export const CookieConsentModal = () => {
	const { t } = useTranslation();
	const { hasConsent, setHasConsent } = useCookieConsent();
	const [analytical, setAnalytical] = useState<boolean>(false);
	const [marketing, setMarketing] = useState<boolean>(false);
	const now = new Date();
	const oneYearFromNow: string = new Date(
		now.setFullYear(now.getFullYear() + 1)
	).toUTCString();

	const setCookie = (name: string, value: string | boolean) => {
		document.cookie = `${name}=${value}; expires=${oneYearFromNow}; path=/; Secure; SameSite=Strict`;
	};

	const handleToggle =
		(setter: React.Dispatch<React.SetStateAction<boolean>>) =>
		(isOn: boolean) => {
			setter(isOn);
		};

	const handleConsent = (analyticalValue: boolean, marketingValue: boolean) => {
		setAnalytical(analyticalValue);
		setMarketing(marketingValue);
		setCookie('analytical', analyticalValue);
		setCookie('marketing', marketingValue);
		setCookie('cookies_consent', true);
		setHasConsent(true);
	};

	const handleAll = () => {
		handleConsent(true, true);
	};

	const handleNecessary = () => {
		handleConsent(false, false);
	};

	const handleChoices = () => {
		handleConsent(analytical, marketing);
	};

	return (
		<ModalShell
			show={!hasConsent}
			size='md'
			center
			icon={<Cookie size={52} />}
			title={t('modal.cookie.title')}
			actions={
				<>
					<BtnLine $small onClick={handleNecessary}>
						{t('modal.cookie.acceptNecessary')}
					</BtnLine>
					<BtnLine $small onClick={handleChoices}>
						{t('modal.cookie.acceptChoices')}
					</BtnLine>
					<BtnTeal $small onClick={handleAll}>
						{t('modal.cookie.acceptAll')}
					</BtnTeal>
				</>
			}
		>
			<p style={{ margin: 0 }}>
				{t('modal.cookie.text')}{' '}
				<TextLink
					to={RoutePath.CookiePolicy}
					style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
				>
					{t('modal.cookie.policyLink')}
				</TextLink>
				.
			</p>

			<CookieOptions>
				<label>
					<Switch isOn={true} disabled />
					{t('modal.cookie.functional')}
				</label>
				<label>
					<Switch onToggle={handleToggle(setAnalytical)} isOn={analytical} />
					{t('modal.cookie.analytical')}
				</label>
				<label>
					<Switch onToggle={handleToggle(setMarketing)} isOn={marketing} />
					{t('modal.cookie.marketing')}
				</label>
			</CookieOptions>
		</ModalShell>
	);
};
